/**
 * transport/cursor.ts —— 事件游标（CONTRACT §4：事件 id 单调，等价 Last-Event-ID 语义）。
 * 纪律：客户端持 cursor；重连以 cursor 请求增量回放；按事件 id 去重（§5 重连幂等）。
 *
 * ★L-AU-FE-T01（审计整改·序3 FE-T-01）：`seen` 原实现只增不清 ⇒ 长会话内存单调增长。
 *   口径：**水位裁剪**——去重集只保最近 SEEN_WATERMARK 个 id（环形截断，先进先出）。
 *   正确性依据（CONTRACT §4/§5）：重连回放请求**只带 `last`（游标值）**，从不枚举 `seen`；
 *   事件 id 单调 ⇒ 裁掉的必是最老 id，其「重复」窗口早已随回放起点推进而失效
 *   ⇒ 裁剪不影响回放正确性，只让极老 id 的重复帧可能被再观察一次（幂等面上无害）。
 */

/** `seen` 去重集水位（裁剪阈值）：500 条 ≈ 重连回放窗口的宽裕上限，防长会话无界增长 */
const SEEN_WATERMARK = 500;

export class EventCursor {
  private seen = new Set<string>();
  private last: string | null = null;

  /**
   * 观察一个事件 id：新 id ⇒ 记录并推进游标（返回 true）；
   * 重复 id ⇒ 去重丢弃（返回 false，重连幂等）。
   * 水位纪律：插入后超阈值 ⇒ 自最老端截断（保最近 SEEN_WATERMARK 个）。
   */
  observe(id: string): boolean {
    if (this.seen.has(id)) return false;
    this.seen.add(id);
    if (this.seen.size > SEEN_WATERMARK) {
      const cutoff = this.seen.size - SEEN_WATERMARK;
      let dropped = 0;
      for (const old of this.seen) {
        if (dropped >= cutoff) break;
        this.seen.delete(old);
        dropped += 1;
      }
    }
    this.last = id;
    return true;
  }

  /** 当前游标（重连回放起点；无事件时 null） */
  value(): string | null {
    return this.last;
  }

  /** 快照帧直接落游标（首帧/回放续点；快照无逐事件 id） */
  adopt(cursor: string): void {
    this.last = cursor;
    if (cursor) this.seen.add(cursor);
  }

  /** 清空（换会话时用） */
  reset(): void {
    this.seen.clear();
    this.last = null;
  }
}
